import React, { useState, useEffect, useRef } from 'react';
import {
  Calculator,
  DollarSign,
  Layers,
  Users,
  BarChart3,
  Target,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import FlipAnalyzerMockup from './mockups/FlipAnalyzerMockup';
import ExpenseTrackerMockup from './mockups/ExpenseTrackerMockup';
import DashboardMockup from './mockups/DashboardMockup';
import TeamViewMockup from './mockups/TeamViewMockup';
import PortfolioMockup from './mockups/PortfolioMockup';
import KanbanMockup from './mockups/KanbanMockup';

const deepDiveFeatures = [
  {
    icon: Calculator,
    name: 'Deal Analysis',
    title: 'Analyze Every Deal in Seconds',
    iconColor: 'text-emerald-400',
    activeColor: 'from-emerald-500 to-emerald-600',
    activeBg: 'bg-emerald-500/10',
    activeBorder: 'border-emerald-500/30',
    description:
      'Enter your purchase price, ARV, and rehab estimate. FlipperPro instantly calculates your net profit, ROI, and Maximum Allowable Offer — so you know your walk-away number before you even call the agent.',
    bullets: [
      'Instant ROI & net profit calculations with holding costs',
      'Sensitivity analysis shows best, expected, and worst scenarios',
      'Save analyses to compare multiple properties side by side',
      'Built-in benchmarks from your own completed flips',
    ],
    Mockup: FlipAnalyzerMockup,
  },
  {
    icon: DollarSign,
    name: 'Expenses',
    title: 'Track Every Dollar of Your Rehab',
    iconColor: 'text-amber-400',
    activeColor: 'from-amber-500 to-amber-600',
    activeBg: 'bg-amber-500/10',
    activeBorder: 'border-amber-500/30',
    description:
      'Track every line item by category, scan receipts with AI, and see real-time budget burn. Get alerts when any category exceeds its budget so you catch overruns before they eat your profit.',
    bullets: [
      'AI-powered receipt scanning auto-categorizes expenses',
      'Real-time budget bars show spending vs. allocation by category',
      'Instant alerts when you approach or exceed budget limits',
      'Export to QuickBooks or CSV with one click',
    ],
    Mockup: ExpenseTrackerMockup,
  },
  {
    icon: Layers,
    name: 'Dashboard',
    title: 'Your Entire Business at a Glance',
    iconColor: 'text-blue-400',
    activeColor: 'from-blue-500 to-blue-600',
    activeBg: 'bg-blue-500/10',
    activeBorder: 'border-blue-500/30',
    description:
      'One dashboard to see every active flip, pending task, upcoming deadline, and financial summary. No switching between apps — everything your business needs is right here.',
    bullets: [
      'Portfolio-wide stats: invested, projected profit, and ROI at a glance',
      'Active project cards with progress bars and risk indicators',
      'Quick actions: analyze a deal, log an expense, or view leads in one click',
      'Recent activity feed and pending approvals widget',
    ],
    Mockup: DashboardMockup,
  },
  {
    icon: Users,
    name: 'Team',
    title: 'Collaborate Without the Chaos',
    iconColor: 'text-violet-400',
    activeColor: 'from-violet-500 to-violet-600',
    activeBg: 'bg-violet-500/10',
    activeBorder: 'border-violet-500/30',
    description:
      "Invite your contractors, realtors, and partners into the same workspace. Assign roles so everyone sees exactly what they need — and nothing they don't.",
    bullets: [
      'Role-based permissions: Owner, Admin, Member, Contractor',
      'Shared project access with granular view/edit controls',
      'Team activity tracking and accountability dashboard',
      'Invite via email or shareable link — onboard in seconds',
    ],
    Mockup: TeamViewMockup,
  },
  {
    icon: BarChart3,
    name: 'Analytics',
    title: "Know What's Actually Working",
    iconColor: 'text-cyan-400',
    activeColor: 'from-cyan-500 to-cyan-600',
    activeBg: 'bg-cyan-500/10',
    activeBorder: 'border-cyan-500/30',
    description:
      "Your completed flips become your playbook. Portfolio analytics show profit trends, ROI by zip code, and rehab cost per sqft — so you double down on what works and avoid what doesn't.",
    bullets: [
      'Sortable portfolio table with profit, ROI, and rehab cost metrics',
      'ROI heatmap by zip code to find your most profitable markets',
      'Historical benchmarks auto-update as you complete more flips',
      'Variance analysis shows where your estimates were off',
    ],
    Mockup: PortfolioMockup,
  },
  {
    icon: Target,
    name: 'Pipeline',
    title: 'From Lead to Acquisition in One Board',
    iconColor: 'text-rose-400',
    activeColor: 'from-rose-500 to-rose-600',
    activeBg: 'bg-rose-500/10',
    activeBorder: 'border-rose-500/30',
    description:
      'Visual pipeline management from lead intake to acquisition. Drag deals through stages, set follow-ups, and get your team aligned on which deals to pursue — so nothing slips through the cracks.',
    bullets: [
      'Drag-and-drop kanban board with customizable stages',
      'Automatic pipeline value and profit calculations',
      'Approval workflows for CFO/CEO deal sign-off',
      'Benchmark comparison against your historical deal data',
    ],
    Mockup: KanbanMockup,
  },
];

export default function FeatureDeepDive({ onSignUp }) {
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const progressRef = useRef(null);
  const tabsRef = useRef(null);

  const ROTATION_INTERVAL = 5000;

  // Auto-rotation
  useEffect(() => {
    if (isPaused) {
      clearInterval(intervalRef.current);
      clearInterval(progressRef.current);
      return;
    }

    setProgress(0);
    const progressStep = 50;
    progressRef.current = setInterval(() => {
      setProgress((p) => Math.min(p + (progressStep / ROTATION_INTERVAL) * 100, 100));
    }, progressStep);

    intervalRef.current = setInterval(() => {
      setActiveTab((t) => (t + 1) % deepDiveFeatures.length);
      setProgress(0);
    }, ROTATION_INTERVAL);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(progressRef.current);
    };
  }, [isPaused, activeTab]);

  const handleTabClick = (index) => {
    setActiveTab(index);
    setIsPaused(true);
    setProgress(0);
    // Resume after 15 seconds of inactivity
    setTimeout(() => setIsPaused(false), 15000);
  };

  const feature = deepDiveFeatures[activeTab];
  const MockupComponent = feature.Mockup;

  return (
    <section className="py-14 px-6 bg-slate-900/40">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
            See It in Action
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto">
            Click through each feature to see exactly what you get inside FlipperPro.
          </p>
        </div>

        {/* Tabs */}
        <div
          ref={tabsRef}
          className="flex gap-1.5 mb-8 overflow-x-auto pb-2 scrollbar-hide justify-start lg:justify-center"
          style={{ overscrollBehavior: 'contain' }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {deepDiveFeatures.map((f, i) => {
            const isActive = i === activeTab;
            return (
              <button
                key={i}
                onClick={() => handleTabClick(i)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 relative overflow-hidden ${
                  isActive
                    ? `bg-gradient-to-r ${f.activeColor} text-white shadow-lg`
                    : 'bg-white/[0.04] text-slate-400 hover:text-white hover:bg-white/[0.08] border border-white/[0.06]'
                }`}
              >
                <f.icon size={16} />
                <span className="hidden sm:inline">{f.name}</span>
                {/* Progress bar under active tab */}
                {isActive && !isPaused && (
                  <div
                    className="absolute bottom-0 left-0 h-0.5 bg-white/30 transition-all duration-100"
                    style={{ width: `${progress}%` }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Content area — fixed height so tab switches don't shift the page */}
        <div
          className="relative min-h-[700px] sm:min-h-[640px] lg:min-h-[420px] overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left: Mockup in browser frame */}
            <div className="order-2 lg:order-1">
              <div
                className={`bg-slate-800/40 rounded-2xl border ${feature.activeBorder} p-1.5 transition-colors duration-500`}
              >
                <div className="bg-slate-900 rounded-xl overflow-hidden">
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                    <div className="flex-1 flex justify-center">
                      <div className="px-3 py-1 bg-white/[0.05] rounded-md text-[10px] text-slate-500 font-medium">
                        app.flipperpro.com/{feature.name.toLowerCase().replace(/\s/g, '-')}
                      </div>
                    </div>
                  </div>
                  {/* Mockup content */}
                  <div className="p-3 bg-gradient-to-br from-slate-800/60 to-slate-900 min-h-[300px]">
                    <MockupComponent size="lg" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Description & bullets */}
            <div className="order-1 lg:order-2 flex flex-col justify-center">
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 ${feature.activeBg} border ${feature.activeBorder} rounded-full mb-4 w-fit transition-colors duration-500`}
              >
                <feature.icon size={14} className={feature.iconColor} />
                <span className={`text-xs font-semibold ${feature.iconColor}`}>{feature.name}</span>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight tracking-tight">
                {feature.title}
              </h3>

              <p className="text-base text-slate-400 leading-relaxed mb-6">{feature.description}</p>

              <div className="space-y-3 mb-8">
                {feature.bullets.map((bullet, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2
                      size={18}
                      className={`${feature.iconColor} mt-0.5 flex-shrink-0`}
                    />
                    <span className="text-sm text-slate-300 leading-relaxed">{bullet}</span>
                  </div>
                ))}
              </div>

              {onSignUp && (
                <button
                  onClick={onSignUp}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-violet-600 text-white text-sm font-bold rounded-xl hover:from-blue-600 hover:to-violet-700 transition-all shadow-lg shadow-violet-500/20 w-fit"
                >
                  Try {feature.name} Free <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
